import styled from 'styled-components';
import hexToRgba from 'hex-rgba';
import { getStyle as get } from '@ids/ddms-styled/comps/textField/intuit/light';

const fontWeight = 500;
const opacity6 = get('field.borderOpacity.disabled') / 100;
const fieldSpacing = '4px';
const inputBorderStroke = get('field.borderStroke');
const inputHighlightStroke = get('field.highlightStroke');
const inputErrorHighlightColor = get('field.highlightColor.error');
const inputBackgroundErrorColor = get('field.backgroundColor.error');
const inputBackgroundColorWarning = get('field.backgroundColor.warning');
const inputWarningHighlightColor = get('field.highlightColor.warning');
const fieldBorderOpacity = get('field.borderOpacity');
const fieldBackgroundOpacity = get('field.backgroundOpacity');
const fieldBackgroundOpacityActive = get('field.backgroundOpacity.active');
const fieldHighlightOpacity = get('field.highlightOpacity');
const productColor = '#0077C5';
const breakpoint = '576px';
const responsiveInputHeight = '40px';
const fieldBackgroundOpacityErrorState = 10; // get('field.backgroundOpacity.error') not found in DDMS data.
const fieldBackgroundOpacityWarningState = 25; // get('field.backgroundOpacity.warning') not found in DDMS data.

const data = {
  intuit: {
    focusColor: productColor,
    hoverBorderColor: productColor
  },
  mint: {
    focusColor: '#15EFE9',
    hoverBorderColor: '#0493a3'
  },
  proconnect: {
    focusColor: productColor,
    hoverBorderColor: productColor
  },
  quickbooks: {
    focusColor: '#2ca01c',
    highlightOpacity: '50',
    hoverBorderColor: '#2ca01c'
  },
  turbotax: {
    focusColor: '#06b6c9',
    hoverBorderColor: '#0493a3'
  }
};

export const InputWrapper = styled.div`
  margin: 0;
  display: flex;
  position: relative;
  ${props => props.disabled && `opacity: ${opacity6};`}
  input.input-no-arrow[type='number'] {
    -moz-appearance: textfield;
  }
  input.input-no-arrow[type='number']::-webkit-inner-spin-button,
  input.input-no-arrow[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const Input = styled.input`
  font-family: inherit;
  font-size: ${get('input.fontSize')} !important;
  font-weight: ${fontWeight};
  line-height: ${get('input.lineHeight')};
  color: ${get('input.color')} !important;
  box-sizing: border-box;
  height: ${get('field.height')} !important;
  width: 100%;
  border: ${inputBorderStroke} solid ${hexToRgba(
  get('field.borderColor'),
  fieldBorderOpacity
)} !important;
  background: ${hexToRgba(
    get('field.backgroundColor'),
    fieldBackgroundOpacity
  )} !important;
  display: block;
  padding: 8px !important;
  border-radius: ${get('field.radius')} !important;
  outline: none;
  &:focus {
    border: ${inputBorderStroke} solid ${hexToRgba(
  productColor,
  fieldBorderOpacity
)} !important;
    box-shadow: 0 0 0 ${inputHighlightStroke} ${hexToRgba(
  productColor,
  fieldHighlightOpacity
)} !important;
  }
  &::placeholder {
    font-weight: ${fontWeight};
    font-style: normal !important;
    color: ${get('placeholder.color')} !important;
  }
  &:hover{
    border-color: ${props =>
      props.theme &&
      data[props.theme] &&
      props.theme === 'quickbooks' &&
      `${data[props.theme].hoverBorderColor} !important`}
  }
  @media only screen and (max-width: ${breakpoint}) {
    padding: 10px !important;
    height: ${responsiveInputHeight};
  }
  ${props =>
    props.theme &&
    data[props.theme] &&
    !props.errorWarningText &&
    `
    &:focus {
      box-shadow: 0 0 0 2px ${hexToRgba(
        data[props.theme].focusColor,
        data[props.theme].highlightOpacity || fieldHighlightOpacity
      )} !important;
      border: ${inputBorderStroke} solid ${
      data[props.theme].focusColor
    } !important;
    }
  `}
  ${props =>
    props.disabled &&
    `
    opacity: ${opacity6};
    cursor: not-allowed !important;
    box-shadow: none !important;
    `}
  ${props =>
    !props.disabled &&
    `
      opacity: ${get('input.opacity')};
    `}
  ${props =>
    !props.disabled &&
    props.errorText &&
    `
    border-color: ${hexToRgba(
      get('field.borderColor.error'),
      fieldBorderOpacity
    )} !important;
    background-color: ${hexToRgba(
      inputBackgroundErrorColor,
      fieldBackgroundOpacityErrorState
    )} !important;
    &:focus {
      border: ${inputBorderStroke} solid ${hexToRgba(
      inputErrorHighlightColor,
      fieldBorderOpacity
    )} !important;
      box-shadow: 0 0 0 ${inputHighlightStroke} ${hexToRgba(
      inputErrorHighlightColor,
      fieldHighlightOpacity
    )} !important;
      background-color: ${hexToRgba(
        inputBackgroundErrorColor,
        fieldBackgroundOpacityActive
      )} !important;
    }
  `}
  ${props =>
    !props.disabled &&
    props.warningText &&
    !props.errorText &&
    `
    border-color: ${hexToRgba(
      get('field.borderColor.warning'),
      fieldBorderOpacity
    )} !important;
    background-color: ${hexToRgba(
      inputBackgroundColorWarning,
      fieldBackgroundOpacityWarningState
    )} !important;
    &:focus {
      border: ${inputBorderStroke} solid ${hexToRgba(
      inputWarningHighlightColor,
      fieldBorderOpacity
    )} !important;
      box-shadow: 0 0 0 ${inputHighlightStroke} ${hexToRgba(
      inputWarningHighlightColor,
      fieldHighlightOpacity
    )} !important;
      background-color: ${hexToRgba(
        inputBackgroundColorWarning,
        fieldBackgroundOpacityActive
      )} !important;
    }
  `}
  ${props =>
    !props.disabled &&
    !props.warningText &&
    !props.errorText &&
    props.validationError &&
    `
    border-color: ${hexToRgba(
      get('field.borderColor.warning'),
      fieldBorderOpacity
    )} !important;
    background-color: ${hexToRgba(
      inputBackgroundColorWarning,
      fieldBackgroundOpacityWarningState
    )} !important;
    &:focus {
      border: ${inputBorderStroke} solid ${hexToRgba(
      inputWarningHighlightColor,
      fieldBorderOpacity
    )} !important;
      box-shadow: 0 0 0 ${inputHighlightStroke} ${hexToRgba(
      inputWarningHighlightColor,
      fieldHighlightOpacity
    )} !important;
      background-color: ${hexToRgba(
        inputBackgroundColorWarning,
        fieldBackgroundOpacityActive
      )} !important;
    }
  `}
  ${props =>
    props.addonBefore &&
    `
    padding-left: 36px !important;
  `}
  ${props =>
    props.addonAfter &&
    `
    padding-right: 36px !important;
  `}
`;

export const TextFieldWrapper = styled.div`
  text-align: left;
  ${props => props.width && `width: ${props.width}`}
  ${props => !props.width && 'width: fit-content'}
`;

export const LabelWrapper = styled.label`
  font-size: ${get('label.fontSize')};
  font-weight: 400;
  line-height: ${get('label.lineHeight')};
  color: ${get('label.color')};
  width: 100%;
  ${props =>
    props.disabled &&
    `
    opacity: ${opacity6};
    cursor: not-allowed;
  `}
  ${props =>
    !props.disabled &&
    `
    opacity: ${get('label.opacity')};
  `}
  ${InputWrapper} {
    ${props =>
      props.label &&
      `
      margin-top: ${fieldSpacing};
    `}
  }
`;

export const GhostTextWrapper = styled.span`
  font-size: ${get('input.fontSize')};
  line-height: ${get('input.lineHeight')};
  font-weight: ${fontWeight};
  font-style: normal;
  left: ${inputBorderStroke};
  position: absolute;
  padding: 8px;
  pointer-events: none;
  text-overflow: clip;
  overflow: hidden;
  width: calc(100% - 8px);
  opacity: ${opacity6};
`;
// helperText.fontStyle needs to be updated
export const HelperTextWrapper = styled.div`
  font-size: ${get('helperText.fontSize')};
  font-style: normal !important;
  font-weight: 400;
  line-height: ${get('helperText.lineHeight')};
  color: ${get('helperText.color')};
  margin-top: ${fieldSpacing};
  ${props =>
    props.disabled &&
    `
    opacity: ${opacity6};
    cursor: not-allowed;
  `}
  ${props =>
    !props.disabled &&
    `
    opacity: ${get('helperText.opacity')};
  `}
`;
